import type { BlacklistAutoRule, BlacklistRuleOperator, CreateBlacklistAutoRule } from '@hprofits/api';
import { CreateBlacklistAutoRuleSchema } from '@hprofits/api';
import type { DictBlacklistBanTypeId, DictBlacklistStatusId } from '@hprofits/shared';

// The new API has no "unset" ban type or period: a stored null type reads back as the dictionary's
// `0` row, so an untouched field is sent the same way.
const UNSET_BAN_TYPE = 0;
const UNSET_CHECK_PERIOD_MIN = 0;

export type BlacklistRuleForm = {
  name: string;
  type: DictBlacklistBanTypeId | null;
  checkPeriodMin: number | null;
  actionState: DictBlacklistStatusId | null;
  /** Explicitly listed ad units; allowed next to a ban type set (they reach Internal-SSP ad units). */
  adUnitIds: Array<number>;
  /** Exceptions to the ban types; contract-requires a non-empty ban type set. */
  excludeAdUnitIds: Array<number>;
  banAdUnitTypeIds: Array<number>;
};
export type ConditionRow = {
  key: string;
  parameter: string;
  function: BlacklistRuleOperator;
  value: string | number | null;
};
export const emptyBlacklistForm = (): BlacklistRuleForm => ({
  banAdUnitTypeIds: [],
  name: '',
  type: null,
  checkPeriodMin: null,
  actionState: null,
  adUnitIds: [],
  excludeAdUnitIds: [],
});
export const toBlacklistForm = (rule: BlacklistAutoRule): BlacklistRuleForm =>
  // Includes, exclusions and ban types load side by side; the payload schema rejects only
  // exclusions without types and an ad unit on both lists.
  ({
    banAdUnitTypeIds: rule.adUnitTypes.map((type) => type.id),
    name: rule.name,
    type: rule.type,
    checkPeriodMin: rule.checkPeriodMin,
    actionState: rule.action.state,
    adUnitIds: rule.adUnits.map((unit) => unit.id),
    excludeAdUnitIds: rule.excludeAdUnits.map((unit) => unit.id),
  });
export const toConditionRows = (rule: BlacklistAutoRule): Array<ConditionRow> =>
  rule.conditions.map((condition, index) => ({
    key: String(index),
    parameter: condition.param,
    function: condition.operator,
    value: condition.value,
  }));
// Parsed with the API's own write schema: it brands the ids and rejects a payload the server would.
export const toBlacklistPayload = (
  form: BlacklistRuleForm,
  rows: ReadonlyArray<ConditionRow>,
): CreateBlacklistAutoRule =>
  CreateBlacklistAutoRuleSchema.parse({
    name: form.name,
    type: form.type ?? UNSET_BAN_TYPE,
    checkPeriodMin: form.checkPeriodMin ?? UNSET_CHECK_PERIOD_MIN,
    action: { state: form.actionState },
    adUnitIds: form.adUnitIds,
    adUnitTypeIds: form.banAdUnitTypeIds,
    excludeAdUnitIds: form.excludeAdUnitIds,
    conditions: rows.map((row) => ({ param: row.parameter, operator: row.function, value: row.value })),
  });
